import { useEffect, useMemo, useState } from "react";
import "../../styles/design-system.css";
import "../features.css";
import { Tag } from "../../components/ui/Tag";
import { Badge } from "../../components/ui/Badge";
import { track } from "../../utils/analytics";
import { useFeatureView } from "../../utils/useFeatureView";

/**
 * Decentralized trial infrastructure — recommends visit modality (clinic /
 * virtual / CHW home) based on a participant's digital access, transportation,
 * and language profile. Designed for digital-divide realities of safety-net
 * populations.
 */

const VISIT_TYPES = [
  {
    id: "clinic",
    name: "In-clinic",
    eyebrow: "FQHC site visit",
    description:
      "Standard PI-led visit at the patient's home FQHC. Default modality when transportation and time-off are not barriers.",
  },
  {
    id: "virtual",
    name: "Telehealth",
    eyebrow: "Video / phone",
    description:
      "Patient joins from a personal device. Requires sufficient broadband or 4G data and device literacy.",
  },
  {
    id: "chw",
    name: "CHW home visit",
    eyebrow: "Community-based",
    description:
      "Trained Community Health Worker conducts assessments at the patient's home; PI joins via tablet for clinical decisions.",
  },
  {
    id: "satellite",
    name: "Satellite collection",
    eyebrow: "Mobile / partner",
    description:
      "Mobile phlebotomy or partner pharmacy collects labs and study product within walking distance of patient.",
  },
];

const VISIT_STEPS = [
  { id: "consent", label: "Informed consent", default: "clinic" },
  { id: "screen", label: "Screening labs", default: "satellite" },
  { id: "baseline", label: "Baseline assessment", default: "clinic" },
  { id: "weekly", label: "Weekly check-ins", default: "virtual" },
  { id: "bimonthly", label: "Bi-monthly clinical", default: "chw" },
  { id: "endpoint", label: "Primary endpoint visit", default: "clinic" },
];

const DEFAULT_PROFILE = {
  broadband: "limited",     // strong | limited | none
  device: "shared",         // own-smartphone | shared | none
  digitalLiteracy: "low",   // high | medium | low
  transport: "limited",     // own | limited | none
  language: "spanish",      // english | spanish | other
  caregiverPresent: true,
};

function recommendModality(profile, step) {
  // Decision rules: prioritize accessibility, fall back to clinic/CHW.
  const noTech =
    profile.broadband === "none" ||
    profile.device === "none" ||
    profile.digitalLiteracy === "low";
  const noTransport = profile.transport === "none";
  const limitedTransport = profile.transport === "limited";

  if (step.id === "consent" || step.id === "endpoint") {
    if (noTransport) return "chw";
    return "clinic";
  }
  if (step.id === "screen" || step.id === "bimonthly") {
    if (noTransport) return "chw";
    if (limitedTransport) return "satellite";
    return "clinic";
  }
  if (step.id === "weekly") {
    if (noTech && noTransport) return "chw";
    if (noTech) return "satellite";
    return "virtual";
  }
  if (step.id === "baseline") {
    if (noTransport) return "chw";
    return "clinic";
  }
  return step.default;
}

function flagAccommodations(profile) {
  const flags = [];
  if (profile.language !== "english") {
    flags.push(`Certified ${profile.language} interpreter at every contact`);
  }
  if (profile.broadband === "none" || profile.broadband === "limited") {
    flags.push("Loaner cellular hotspot + data plan for trial duration");
  }
  if (profile.device === "none") {
    flags.push("Loaner tablet pre-configured with study app");
  }
  if (profile.digitalLiteracy === "low") {
    flags.push("CHW-led tech onboarding, week 0 + week 1");
  }
  if (profile.transport === "limited") {
    flags.push("Pre-paid rideshare vouchers for clinic visits");
  }
  if (profile.transport === "none") {
    flags.push("All clinical contacts shifted to CHW home visits");
  }
  return flags;
}

const SELECT_OPTIONS = {
  broadband: [
    { v: "strong", l: "Strong / home wifi" },
    { v: "limited", l: "Limited / 4G only" },
    { v: "none", l: "None" },
  ],
  device: [
    { v: "own-smartphone", l: "Own smartphone" },
    { v: "shared", l: "Shared household device" },
    { v: "none", l: "No device" },
  ],
  digitalLiteracy: [
    { v: "high", l: "High" },
    { v: "medium", l: "Medium" },
    { v: "low", l: "Low" },
  ],
  transport: [
    { v: "own", l: "Owns vehicle" },
    { v: "limited", l: "Public transit / occasional" },
    { v: "none", l: "No reliable transport" },
  ],
  language: [
    { v: "english", l: "English" },
    { v: "spanish", l: "Spanish" },
    { v: "other", l: "Other" },
  ],
};

export function DecentralizedTrials() {
  const sectionRef = useFeatureView("decentralized_trials");
  const [profile, setProfile] = useState(DEFAULT_PROFILE);

  useEffect(() => {
    const decentralized = schedule.filter((r) => r.modalityId !== "clinic").length;
    track("dct_schedule_view", {
      broadband: profile.broadband,
      transport: profile.transport,
      language: profile.language,
      decentralizedSteps: decentralized,
      accommodationCount: accommodations.length,
    });
  }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

  const schedule = useMemo(() => {
    return VISIT_STEPS.map((step) => ({
      step,
      modalityId: recommendModality(profile, step),
    })).map((row) => ({
      ...row,
      modality: VISIT_TYPES.find((v) => v.id === row.modalityId),
    }));
  }, [profile]);

  const accommodations = useMemo(() => flagAccommodations(profile), [profile]);

  function update(key, value) {
    setProfile((prev) => ({ ...prev, [key]: value }));
  }

  return (
    <section ref={sectionRef} className="ds-section feature-section" id="feature-decentralized">
      <div className="ds-page">
        <div className="ds-section-head">
          <div className="ds-section-num">04 / 05</div>
          <div>
            <h2 className="ds-section-title">Decentralized trial infrastructure</h2>
            <p className="ds-section-sub">
              Hybrid in-person / virtual / CHW visit routing. Built for the
              digital-divide realities of safety-net populations — accommodations
              are surfaced, not assumed.
            </p>
          </div>
        </div>

        <div className="ds-grid-2 feature-spaced">
          <div className="ds-card">
            <div className="ds-card-label">
              <span>PARTICIPANT ACCESS PROFILE</span>
              <span>{profile.language.toUpperCase()}</span>
            </div>
            <div className="feature-form-grid">
              {Object.entries(SELECT_OPTIONS).map(([key, options]) => (
                <label key={key} className="ds-input-label">
                  {key.replace(/([A-Z])/g, " $1")}
                  <select
                    className="ds-input"
                    value={profile[key]}
                    onChange={(e) => update(key, e.target.value)}
                  >
                    {options.map((o) => (
                      <option key={o.v} value={o.v}>{o.l}</option>
                    ))}
                  </select>
                </label>
              ))}
            </div>
            <div className="feature-spaced feature-checkboxes">
              <label>
                <input
                  type="checkbox"
                  checked={profile.caregiverPresent}
                  onChange={(e) => update("caregiverPresent", e.target.checked)}
                />
                Caregiver available for visits
              </label>
            </div>
          </div>

          <div className="ds-card">
            <div className="ds-card-label">
              <span>ACCOMMODATIONS · AUTO</span>
              <span>{accommodations.length}</span>
            </div>
            {accommodations.length === 0 ? (
              <p className="feature-help">No accommodations needed.</p>
            ) : (
              <ul className="feature-referrals">
                {accommodations.map((a) => (
                  <li key={a}>
                    <span className="feature-bullet">/</span> {a}
                  </li>
                ))}
              </ul>
            )}
            <p className="feature-help feature-spaced">
              Accommodations are pre-loaded into the participant's visit packet
              and the CHW dispatch board. Sponsors see anonymized counts in the
              equity dashboard.
            </p>
          </div>
        </div>

        <div className="feature-spaced">
          <div className="ds-card-label">
            <span>RECOMMENDED VISIT SCHEDULE</span>
            <span>{schedule.length} STEPS</span>
          </div>
          <div className="feature-schedule">
            {schedule.map(({ step, modality }) => (
              <div key={step.id} className="feature-schedule-row">
                <div className="feature-schedule-step">
                  <span className="feature-schedule-num">{step.id}</span>
                  <strong>{step.label}</strong>
                </div>
                <div className="feature-schedule-modality">
                  <Tag variant="slash">/ {modality.name}</Tag>
                  <div className="feature-schedule-eyebrow">{modality.eyebrow}</div>
                </div>
                <Badge status={modality.id === "clinic" ? "live" : "draft"}>
                  {modality.id === "clinic" ? "Site" : "Decentralized"}
                </Badge>
              </div>
            ))}
          </div>
        </div>

        <div className="ds-grid-4 feature-spaced">
          {VISIT_TYPES.map((v) => (
            <div key={v.id} className="ds-card">
              <div className="ds-card-label">
                <span>{v.eyebrow}</span>
                <span>·</span>
              </div>
              <h3 className="feature-program-name">{v.name}</h3>
              <p className="feature-program-desc">{v.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default DecentralizedTrials;
